import { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Modal, Segmented, Textarea } from '@indipia/ui-core';
import {
  SHIP_RECEIPT_NOTE_MAX, SHIP_RECEIPT_TEXT, shipReceiptBody, shipReceiptFormErrors, shipReceiptFormInit,
  type ShipReceiptForm,
} from '../../constants/ship.logic';
import type { ShipReceiptBody } from '../../constants/ship.logic';

interface Props {
  /** 확인 창 제목 아래에 보이는 대상 한 줄(공종 · 자재파트너) */
  subject: string;
  /** YYYY-MM-DD(서울 오늘) */
  today: string;
  busy: boolean;
  /** 제출 실패 문구(서버 문구가 아니라 화면 문구) */
  error: string | null;
  onSubmit: (body: ShipReceiptBody) => void;
  onClose: () => void;
}

/**
 * 담당자 직접 입고 확인 창(C6, 사진 없음) — 수량 확인(이상 없음/부족·파손) · 부족·파손이면 내용 필수 300자 · 입고일(기본 오늘, 미래·30일 이전 불가).
 * 부모가 조건부로 마운트하고(열 때마다 새 입력값), 닫는 동안 busy 면 닫지 않는다.
 */
export function ShipReceiptConfirmModal({ subject, today, busy, error, onSubmit, onClose }: Props) {
  const [form, setForm] = useState<ShipReceiptForm>(() => shipReceiptFormInit(today));
  const [tried, setTried] = useState(false);
  const errors = useMemo(() => shipReceiptFormErrors(form, today), [form, today]);
  const showErr = tried;

  const submit = () => {
    setTried(true);
    const body = shipReceiptBody(form, today);
    if (body) onSubmit(body);
  };

  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title={SHIP_RECEIPT_TEXT.title}
      size="sm"
      footerAlign="stretch"
      footer={<>
        <Button variant="secondary" disabled={busy} onClick={onClose}>{SHIP_RECEIPT_TEXT.cancel}</Button>
        <Button variant="primary" loading={busy} disabled={busy} onClick={submit}>{SHIP_RECEIPT_TEXT.submit}</Button>
      </>}
    >
      <div className="flex flex-col gap-3">
        <p className="text-caption text-text-muted">{subject}</p>

        <div className="flex flex-col gap-1">
          <div id="ship-receipt-qty-label" className="text-body-sm font-semibold text-text-primary">{SHIP_RECEIPT_TEXT.qtyLabel}</div>
          <div role="group" aria-labelledby="ship-receipt-qty-label">
            <Segmented
              size="sm"
              value={form.qty}
              options={[
                { label: SHIP_RECEIPT_TEXT.qtyNormal, value: 'NORMAL' },
                { label: SHIP_RECEIPT_TEXT.qtyIssue, value: 'ISSUE' },
              ]}
              onChange={v => setForm(f => ({ ...f, qty: v === 'ISSUE' ? 'ISSUE' : 'NORMAL' }))}
            />
          </div>
          {showErr && errors.qty && <p className="text-caption text-error" role="alert">{errors.qty}</p>}
        </div>

        {form.qty === 'ISSUE' && (
          <div>
            <Textarea
              id="ship-receipt-note"
              label={SHIP_RECEIPT_TEXT.noteLabel}
              className="!max-w-none"
              value={form.issueNote}
              error={showErr ? errors.issueNote : undefined}
              onChange={e => setForm(f => ({ ...f, issueNote: e.target.value }))}
            />
            <p className="mt-1 text-right text-caption text-text-muted" aria-hidden="true">
              {SHIP_RECEIPT_TEXT.noteCount(form.issueNote.length, SHIP_RECEIPT_NOTE_MAX)}
            </p>
          </div>
        )}

        <DatePicker
          id="ship-receipt-date"
          label={SHIP_RECEIPT_TEXT.dateLabel}
          size="sm"
          value={form.receiptDate}
          error={errors.receiptDate}
          onChange={d => setForm(f => ({ ...f, receiptDate: d }))}
        />

        {error && <Alert kind="error" role="alert">{error}</Alert>}
      </div>
    </Modal>
  );
}
